import React, { useContext, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import LogoutConfirmDialog from "./LogoutConfirmDialog";

export default function SidebarAccountMenu({ name, role, initials, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);
  const root = useRef(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const menuId = useId();
  const navigate = useNavigate();
  const { dispatch } = useContext(AuthContext);

  useEffect(() => {
    if (!open) return undefined;
    menu.current?.querySelector('[role="menuitem"]')?.focus();
    const outside = event => {
      if (!root.current?.contains(event.target)) setOpen(false);
    };
    const escape = event => {
      if (event.key === "Escape") {
        setOpen(false);
        trigger.current?.focus();
        event.stopPropagation();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const go = path => {
    setOpen(false);
    onNavigate?.();
    navigate(path);
  };
  const logout = () => {
    localStorage.removeItem("user");
    sessionStorage.removeItem("user");
    dispatch({ type: "LOGOUT" });
    go("/login");
  };

  return <><div ref={root} className="relative shrink-0 border-t border-slate-200 bg-white p-3"
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false); }}>
    {open && <div ref={menu} id={menuId} role="menu" aria-label="Account actions"
      className="absolute bottom-full left-3 right-3 z-50 mb-2 overflow-hidden rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl"
      onKeyDown={event => {
        const items = Array.from(menu.current.querySelectorAll('[role="menuitem"]'));
        const index = items.indexOf(document.activeElement);
        let next;
        if (event.key === "ArrowDown") next = (index + 1) % items.length;
        if (event.key === "ArrowUp") next = (index - 1 + items.length) % items.length;
        if (event.key === "Home") next = 0;
        if (event.key === "End") next = items.length - 1;
        if (next !== undefined) { event.preventDefault(); items[next].focus(); }
      }}>
      <div className="mb-1 border-b border-slate-100 px-3 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Signed in as</p>
        <p className="mt-1 truncate text-sm font-semibold text-slate-900">{name || "Logged in user"}</p>
        <p className="mt-0.5 truncate text-xs text-slate-500">{role || "User"}</p>
      </div>
      <button type="button" role="menuitem" onClick={() => go("/checkuser")}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-slate-700 hover:bg-indigo-50 focus:bg-indigo-50 focus:outline-none">
        <svg aria-hidden="true" className="h-5 w-5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="8" r="3.5"/><path d="M5 21v-2a7 7 0 0 1 14 0v2"/></svg>
        Manage My Account
      </button>
      <button type="button" role="menuitem" onClick={() => { setOpen(false); setConfirmLogout(true); }}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm font-medium text-rose-600 hover:bg-rose-50 focus:bg-rose-50 focus:outline-none">
        <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M9 4H4v16h5M9 12h12m-4-4 4 4-4 4"/></svg>
        Logout
      </button>
    </div>}
    <button ref={trigger} type="button" aria-label="Open account menu" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? menuId : undefined}
      onClick={() => setOpen(value => !value)}
      onKeyDown={event => { if (event.key === "ArrowUp" || event.key === "ArrowDown") { event.preventDefault(); setOpen(true); } }}
      className={`flex w-full items-center gap-3 rounded-xl p-2 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${open ? "bg-indigo-50" : ""}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-950 to-indigo-600 text-xs font-semibold text-white">{initials}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-slate-900">{name || "Logged in user"}</span>
        <span className="mt-0.5 block truncate text-[10px] font-semibold uppercase tracking-wider text-slate-500">{role || "User"}</span>
      </span>
      <svg aria-hidden="true" className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="m6 14 6-6 6 6"/></svg>
    </button>
  </div>
    <LogoutConfirmDialog open={confirmLogout} onConfirm={logout} onCancel={() => {
      setConfirmLogout(false);
      trigger.current?.focus();
    }} />
  </>;
}
